import { useEffect } from 'react';

const SEO = ({ 
  title = "WOW AUTOBODY - Professional Auto Body Repair & Collision Services",
  description = "Expert auto body repair, collision repair, dent removal, and painting services. Serving the community since 2010 with ASE certified technicians. Free estimates, insurance claims assistance.",
  keywords = "auto body repair, collision repair, dent removal, car painting, auto body shop, insurance claims, scratch repair, vehicle restoration",
  image = "https://wowautobody.local/og-image.jpg",
  url = "https://wowautobody.local",
  type = "website",
  author = "WOW AUTOBODY",
  robots = "index, follow",
  canonical = null
}) => {
  
  useEffect(() => {
    // Update document title
    document.title = title;

    // Remove existing meta tags (both with and without data-seo attribute)
    const existingTags = document.querySelectorAll('meta[data-seo="true"]');
    existingTags.forEach(tag => tag.remove());
    
    // Also remove the default meta description from index.html
    const defaultDescription = document.querySelector('meta[name="description"]:not([data-seo])');
    if (defaultDescription) {
      defaultDescription.remove();
    }

    // Helper function to create meta tags
    const createMetaTag = (attrs) => {
      const tag = document.createElement('meta');
      Object.keys(attrs).forEach(key => {
        if (key !== 'data-seo') {
          tag.setAttribute(key, attrs[key]);
        }
      });
      tag.setAttribute('data-seo', 'true');
      return tag;
    };

    // Basic SEO Meta Tags
    const metaTags = [
      // Basic Meta Tags
      { name: 'description', content: description },
      { name: 'keywords', content: keywords },
      { name: 'author', content: author },
      { name: 'robots', content: robots },
      { name: 'viewport', content: 'width=device-width, initial-scale=1.0' },
      
      // Open Graph Tags
      { property: 'og:title', content: title },
      { property: 'og:description', content: description },
      { property: 'og:image', content: image },
      { property: 'og:url', content: url },
      { property: 'og:type', content: type },
      { property: 'og:site_name', content: 'WOW AUTOBODY' },
      { property: 'og:locale', content: 'en_US' },
      
      // Twitter Card Tags
      { name: 'twitter:card', content: 'summary_large_image' },
      { name: 'twitter:title', content: title },
      { name: 'twitter:description', content: description },
      { name: 'twitter:image', content: image },
      
      // Business Specific
      { name: 'geo.region', content: 'US' },
      { name: 'geo.placename', content: 'Springfield' },
      { name: 'geo.position', content: '39.7817;-89.6501' },
      { name: 'ICBM', content: '39.7817, -89.6501' },
      
      // Mobile
      { name: 'mobile-web-app-capable', content: 'yes' },
      { name: 'apple-mobile-web-app-capable', content: 'yes' },
      { name: 'apple-mobile-web-app-status-bar-style', content: 'default' },
      { name: 'apple-mobile-web-app-title', content: 'WOW AUTOBODY' },
      
      // Theme Colors
      { name: 'theme-color', content: '#1e3a8a' },
      { name: 'msapplication-TileColor', content: '#1e3a8a' },
      
      // Additional SEO
      { name: 'language', content: 'en' },
      { name: 'revisit-after', content: '7 days' },
      { name: 'rating', content: 'general' }
    ];

    // Add meta tags to head
    const head = document.head;
    metaTags.forEach(attrs => {
      head.appendChild(createMetaTag(attrs));
    });

    // Add canonical link if provided
    if (canonical) {
      const existingCanonical = document.querySelector('link[rel="canonical"]');
      if (existingCanonical) {
        existingCanonical.remove();
      }
      
      const canonicalLink = document.createElement('link');
      canonicalLink.rel = 'canonical';
      canonicalLink.href = canonical;
      canonicalLink.setAttribute('data-seo', 'true');
      head.appendChild(canonicalLink);
    }

    // Add structured data for local business
    const structuredData = {
      "@context": "https://schema.org",
      "@type": "AutoRepair",
      "name": "WOW AUTOBODY",
      "image": image,
      "description": description,
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "123 Auto Repair Lane",
        "addressLocality": "Springfield",
        "addressRegion": "ST",
        "postalCode": "12345",
        "addressCountry": "US"
      },
      "geo": {
        "@type": "GeoCoordinates",
        "latitude": 39.7817,
        "longitude": -89.6501
      },
      "url": url,
      "telephone": "(555) 123-4567",
      "email": "info@wowautobody.com",
      "priceRange": "$$",
      "openingHoursSpecification": [
        {
          "@type": "OpeningHoursSpecification",
          "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
          "opens": "08:00",
          "closes": "18:00"
        },
        {
          "@type": "OpeningHoursSpecification",
          "dayOfWeek": "Saturday",
          "opens": "09:00",
          "closes": "16:00"
        }
      ],
      "paymentAccepted": "Cash, Credit Card, Insurance",
      "aggregateRating": {
        "@type": "AggregateRating",
        "ratingValue": "4.9",
        "reviewCount": "150"
      },
      "hasOfferCatalog": {
        "@type": "OfferCatalog",
        "name": "Auto Body Services",
        "itemListElement": [
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "Collision Repair",
              "description": "Complete collision damage repair using state-of-the-art equipment"
            }
          },
          {
            "@type": "Offer", 
            "itemOffered": {
              "@type": "Service",
              "name": "Dent Removal",
              "description": "Professional paintless dent repair and scratch removal"
            }
          },
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service", 
              "name": "Auto Painting",
              "description": "Expert automotive painting with premium quality finishes"
            }
          }
        ]
      },
      "founder": {
        "@type": "Person",
        "name": "WOW AUTOBODY Team"
      },
      "foundingDate": "2010",
      "areaServed": {
        "@type": "GeoCircle",
        "geoMidpoint": {
          "@type": "GeoCoordinates",
          "latitude": 39.7817,
          "longitude": -89.6501
        },
        "geoRadius": "50000"
      }
    };

    // Remove existing structured data
    const existingStructuredData = document.querySelector('script[data-seo="structured-data"]');
    if (existingStructuredData) {
      existingStructuredData.remove();
    }

    // Add structured data script
    const scriptTag = document.createElement('script');
    scriptTag.type = 'application/ld+json';
    scriptTag.setAttribute('data-seo', 'structured-data');
    scriptTag.textContent = JSON.stringify(structuredData);
    head.appendChild(scriptTag);

    // Cleanup function
    return () => {
      const tagsToRemove = document.querySelectorAll('[data-seo="true"]');
      tagsToRemove.forEach(tag => tag.remove());
    };
  }, [title, description, keywords, image, url, type, author, robots, canonical]);

  return null; // This component doesn't render anything
};

export default SEO;